import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Icon from '@common/assets';
import { useTermsCheckNewMutation } from '@components/acca/test/query/useTerms';
import { useAccMaxApplyChance, useApplyHistoryList } from '@repository/acc/useAcca';
import { useUserInfo } from '@repository/auth/useUserInfo';
import AccaModal from '../modals';
import { useApplyTutorial } from '../query/useApply';
import { AccaModalType } from '@domain/constant/acca/AccaModalType';
import { TERMS } from '@domain/constant/termsType';
import { RouteUrl, V2Route } from '@utils/routeUtil';
import { isEmpty } from '@utils/common';
import { useAffilliateCertificationStatus } from '@repository/partnership/usePartnership';
import RouterQuery, { UtmOrigin } from '@utils/routeUtil/RouterQuery';
import classnames from 'classnames/bind';
import styles from './serviceList.module.scss';
import Colors from '@domain/constant/colors';
import UrlUtil from '@utils/urlUtil';
import useKcbIdentity from '@utils/hooks/useKcbIdentity';
const cx = classnames.bind(styles);

const ServiceList = () => {
  const { query } = useRouter();
  const [modalState, setModalState] = useState<AccaModalType>(null);

  const { data: userInfo } = useUserInfo();
  const isLogin = !!userInfo?.id;

  const { data: applyHistoryData } = useApplyHistoryList(isLogin);
  const { data: maxApplyChanceData } = useAccMaxApplyChance();
  const chanceCount = applyHistoryData?.chanceCount ?? 0;
  const maxChanceCount = applyHistoryData?.chanceCount ?? maxApplyChanceData;

  const { data: allianceData } = useAffilliateCertificationStatus();
  const isVerifiedUniversity = allianceData?.universityVerified;
  const isVerifiedOrganization = allianceData?.organizationVerified;

  const { isVerification } = useKcbIdentity({
    onError: () => setModalState(null),
    onSuccess: () => setModalState('TEST_TERMS'),
  });
  const { mutateAsync: checkTermsMutateAsync } = useTermsCheckNewMutation();
  const { data: tutorialApplyData } = useApplyTutorial(isLogin);

  const onClickTestBtn = async () => {
    //로그인 여부 체크
    if (!isLogin) {
      setModalState('LOGIN');
      return;
    }

    if (!chanceCount) {
      setModalState('NO_MORE_CHANCE');
      return;
    }

    //본인 인증 체크
    if (!isVerification) {
      setModalState('VERIFICATION');
      return;
    }

    //약관 동의 여부 체크
    const { existsNewTermsAgree } = await checkTermsMutateAsync(TERMS.ACC_PRIVACY_POLICY);
    if (existsNewTermsAgree) {
      setModalState('TEST_TERMS');
      return;
    }

    const nextUrl = isEmpty(query)
      ? RouteUrl.ACCA_TEST_LIST
      : `${RouteUrl.ACCA_TEST_LIST}?${new URLSearchParams(query as Record<string, string>).toString()}`;

    window.open(nextUrl);
  };

  //튜토리얼 페이지 열기
  const onApplyTutorial = async () => {
    const { url, token } = tutorialApplyData;
    const accTutorialUrl = V2Route.getAccaTutorialPage(url);
    UrlUtil.openWindowWithPost(accTutorialUrl, { Authorization: `Bearer ${token}` });
  };

  const onClickTutorialApplyBtn = async () => {
    if (!isLogin) {
      setModalState('LOGIN');
      return;
    }

    //약관 동의 여부 체크
    const { existsNewTermsAgree } = await checkTermsMutateAsync(TERMS.ACC_TRIAL_PRIVACY_POLICY);
    if (existsNewTermsAgree) {
      setModalState('TUTORIAL_TERMS');
      return;
    }

    onApplyTutorial();
  };

  return (
    <>
      <div className={cx('serviceListArea')}>
        <button className={cx('btn')} onClick={onClickTutorialApplyBtn}>
          <div className={cx('textArea')}>튜토리얼 연습하기</div>
          <Icon name="arrowRightLight" width={48} height={48} fill={Colors.C_JOBDA_BLACK} />
        </button>

        <button className={cx('btn')} onClick={onClickTestBtn}>
          <div className={cx('textArea')}>
            {(isVerifiedUniversity || isVerifiedOrganization) && <div className={cx('certification')}>제휴인증</div>}
            응시하기
            <span className={cx('count')}> (남은 횟수 : {maxChanceCount}회)</span>
          </div>
          <Icon name="arrowRightLight" width={48} height={48} fill={Colors.C_JOBDA_BLACK} />
        </button>

        <Link
          href={{ pathname: RouteUrl.ACCA_RESULTS, query: RouterQuery.getUtmSource(UtmOrigin.ACCA) }}
          legacyBehavior
        >
          <a className={cx('btn')}>
            결과표 확인하기
            <Icon name="arrowRightLight" width={48} height={48} fill={Colors.C_JOBDA_BLACK} />
          </a>
        </Link>
      </div>

      <AccaModal modalState={modalState} setModalState={setModalState} />
    </>
  );
};

export default ServiceList;
